'use client'

import { RichText } from '@/components/shared/rich-text'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { fadeInBlur } from '@/lib/animations'
import { MainHeroStoryblok } from '@/lib/types/component-types-sb'
import { storyblokEditable } from '@storyblok/react/rsc'
import { Check, Copy } from 'lucide-react'
import { motion } from 'motion/react'
import { useState } from 'react'
import ReactPlayer from 'react-player'
import { StoryblokImage } from '../shared/storyblok-image'
import { StoryblokLink } from '../shared/storyblok-link'
import { BorderBeam } from '../ui/border-beam'

export function MainHero({ blok }: { blok: MainHeroStoryblok }) {
  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const firstButton = blok.buttons?.at(0)
  const secondButton = blok.buttons?.at(1)

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText('npx @vendure/create my-shop')
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy command:', err)
    }
  }

  return (
    <section
      {...storyblokEditable(blok)}
      className="overflow-hidden py-12 lg:pt-30 lg:pb-6"
    >
      <div className="page-container">
        <div className="relative z-10 flex flex-col items-center text-center">
          <motion.h1
            {...fadeInBlur}
            className="text-brand mb-3 block font-sans text-sm font-semibold tracking-widest uppercase"
            transition={{ duration: 0.5 }}
          >
            {blok.preTitle}
          </motion.h1>
          <motion.span
            {...fadeInBlur}
            transition={{ duration: 0.7 }}
            className="font-heading text-accent-foreground tracking-hero-title mb-3 block text-4xl leading-none font-semibold lg:max-w-[60%] lg:text-5xl"
          >
            {blok.title.split('\n').map((line, index) => (
              <span
                key={index}
                className="lg:block"
              >
                {line}
              </span>
            ))}
          </motion.span>
          <motion.div
            {...fadeInBlur}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="mb-6 lg:max-w-1/2"
          >
            <RichText
              className="text-lg"
              content={blok.content}
            />
          </motion.div>
          <motion.div
            {...fadeInBlur}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex items-center justify-center gap-2"
          >
            {firstButton?.target && (
              <Button
                asChild
                size="lg"
                key={firstButton._uid}
                variant="default"
              >
                <StoryblokLink link={firstButton.target}>{firstButton.label}</StoryblokLink>
              </Button>
            )}
            {secondButton?.target && (
              <Button
                asChild
                size="lg"
                key={secondButton._uid}
                variant="outline"
              >
                <StoryblokLink link={secondButton.target}>{secondButton.label}</StoryblokLink>
              </Button>
            )}
          </motion.div>
          <motion.div
            {...fadeInBlur}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="mt-4 flex items-center justify-center"
          >
            <div className="bg-muted/20 border-border/30 relative flex items-center gap-3 rounded-md border px-3 py-2 font-mono text-xs">
              <span className="text-muted-foreground/70">$</span>
              <span className="text-muted-foreground/80">npx @vendure/create my-shop</span>
              <button
                onClick={handleCopyCommand}
                className="text-muted-foreground/60 hover:text-muted-foreground/80 transition-colors duration-200"
                aria-label="Copy command"
              >
                {isCopied ? (
                  <Check className="h-3 w-3 text-green-500/80" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            </div>
          </motion.div>
        </div>
        {blok.visual && (
          <div className="relative flex justify-center lg:mx-0">
            <StoryblokImage
              image={blok.visual}
              cropWidth={1600}
              priority
              className="h-auto w-[600px] max-w-[600px] lg:w-full lg:max-w-full"
            />
            {blok.videoUrl && (
              <Dialog
                open={isVideoOpen}
                onOpenChange={setIsVideoOpen}
              >
                <DialogTrigger asChild>
                  <button
                    className="group absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer flex-col items-center gap-3 transition-transform ease-in-out"
                    aria-label="Play video"
                  >
                    <div className="bg-brand/80 shadow-brand/10 relative flex h-20 w-20 items-center justify-center rounded-full shadow-lg backdrop-blur-2xl duration-300 ease-in-out group-hover:scale-110">
                      <svg
                        className="text-accent-foreground h-12 w-12"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                    {blok.playButtonLabel && (
                      <span className="text-foreground bg-background border-border drop-shadow-brand/20 relative block shrink-0 rounded-lg border px-3 py-1 text-sm font-medium drop-shadow-xl backdrop-blur-sm lg:text-xl">
                        {blok.playButtonLabel}
                        <BorderBeam
                          duration={8}
                          size={40}
                          colorFrom="var(--color-brand-lighter)"
                          colorTo="var(--color-brand-lighter)"
                        />
                      </span>
                    )}
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-sidebar min-w-[80%] overflow-hidden p-2 md:min-w-4xl">
                  <DialogTitle className="sr-only">{blok.playButtonLabel}</DialogTitle>
                  <div className="border-border relative aspect-video w-full rounded border">
                    <ReactPlayer
                      width="100%"
                      height="100%"
                      url={blok.videoUrl}
                      playing={isVideoOpen}
                      controls
                    />
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
